/**
 * MODEL mode. Two mounts share this file because they share the project vocabulary:
 *  - `mountModelPanel`       (left)  import a GLB, see where you are in the workflow
 *  - `mountProjectListPanel` (right) the persisted projects: open / delete / close
 */

import type { ProjectRecord } from '../../shared';
import type { EditorContext } from '../context';
import type { EditorState } from '../index';
import { canEnterMode } from '../index';
import { dialogs } from '../dialogs';
import { button, el, fileButton, icon, iconButton } from './dom';
import {
  createPanel,
  note,
  readonlyField,
  runAsync,
  section,
  type PanelHandle,
} from './panel';
import type { Controllers } from '../controllers/types';
import './rigmap.css';

function fileFrom(list: FileList | null | undefined): File | null {
  return list && list.length > 0 ? list[0] : null;
}

/**
 * The five stages of the product, in order. Informational only — the toolbar's mode segments are
 * what switches modes, so these rows are never controls (see `rigmap.css`).
 */
const WORKFLOW: readonly string[] = [
  'Import a character (.glb)',
  'Rig it — or Map its existing bones',
  'Animate: pose, key, play',
  'Library: clips shared by every character',
  'Export: rig-only GLB + clip files',
];

/**
 * Which step to highlight. Only the first three are ever "current": the card answers "what do I
 * do next", and 4–5 are always available rather than being a stage you arrive at.
 */
function workflowStage(state: EditorState): number {
  if (!state.project) return 1;
  return canEnterMode(state, 'animate') ? 3 : 2;
}

/** When the project list last saw this project, `0` when neither timestamp is usable. */
function recency(project: ProjectRecord): number {
  const value = project.lastOpenedAt ?? project.updatedAt;
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Compact "how long ago", because a full locale timestamp on every row is unreadable at a glance
 * and the exact minute of an open is not information anybody acts on. Anything older than a week
 * falls back to a date — "9 d ago" stops being easier to read than the day itself.
 */
function relativeTime(at: number, now: number = Date.now()): string {
  const ms = now - at;
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  if (ms < 60_000) return 'just now';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(ms / 86_400_000);
  if (days < 7) return `${days} d ago`;
  return new Date(at).toLocaleDateString();
}

/** 1024-based, one decimal from a megabyte up — the sizes here are GLBs, not round numbers. */
function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

/** `opened 3 h ago · 14.3 MB`; the size is dropped when the blob could not be measured. */
function projectMeta(project: ProjectRecord, size: number | undefined): string {
  const opened =
    project.lastOpenedAt === undefined
      ? 'never opened'
      : `opened ${relativeTime(project.lastOpenedAt)}`;
  return size === undefined ? opened : `${opened} · ${formatBytes(size)}`;
}

export function mountModelPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store, session } = ctx;

  function importFile(file: File | null): void {
    if (!file) return;
    runAsync(controllers.project.importGlbFile(file, file.name.replace(/\.glb$/i, '')));
  }

  function render(root: HTMLElement): void {
    const state = store.getState();
    const model = session.model;

    /*
     * The drop zone keeps its own handlers: the shell's window-level drop ignores a drop whose
     * target is inside a `.dropzone`, so this one stays the handler that fires here.
     */
    const drop = el('div', { class: 'dropzone', text: 'or drop a .glb anywhere in the window' });
    drop.addEventListener('dragover', (event) => {
      event.preventDefault();
      drop.classList.add('over');
    });
    drop.addEventListener('dragleave', () => drop.classList.remove('over'));
    drop.addEventListener('drop', (event) => {
      event.preventDefault();
      drop.classList.remove('over');
      importFile(fileFrom((event as DragEvent).dataTransfer?.files));
    });

    root.appendChild(
      section(
        'Import',
        [
          // A native file input renders its own button *and* a filename label; neither fits a
          // 320px column, so the input hides inside the `.filebtn` label instead.
          fileButton('Choose .glb…', '.glb', (file) => importFile(file), {
            'data-role': 'glb-input',
          }),
          drop,
        ],
        { icon: 'upload' },
      ),
    );

    const stage = workflowStage(state);
    root.appendChild(
      section(
        'Workflow',
        [
          el(
            'div',
            { class: 'steps' },
            WORKFLOW.map((label, index) =>
              el('div', { class: index + 1 === stage ? 'step on' : 'step' }, [
                el('span', { class: 'n', text: String(index + 1) }),
                label,
              ]),
            ),
          ),
        ],
        { icon: 'box' },
      ),
    );

    const info: HTMLElement[] = [];
    if (!state.project) {
      info.push(note('No project open. Import a GLB to start.'));
    } else {
      info.push(readonlyField('Project', state.project.name));
      info.push(readonlyField('Kind', state.project.kind));
      info.push(readonlyField('Loaded', state.project.loaded ? 'yes' : 'no'));
      if (model) {
        info.push(readonlyField('Bones', String(model.sourceBoneNames.length)));
        info.push(
          readonlyField('Meshes', String(model.meshes.length + model.skinnedMeshes.length)),
        );
        info.push(readonlyField('Height', `${model.height.toFixed(3)} m`));
      }
      info.push(
        button([icon('trash'), 'Close project'], () => controllers.project.closeProject()),
      );
    }
    root.appendChild(section('Current model', info, { icon: 'box' }));

    if (model && model.warnings.length > 0) {
      const list = el('ul', { class: 'warnlist' });
      for (const warning of model.warnings) list.appendChild(el('li', { text: warning }));
      root.appendChild(
        section('Import warnings', [list], {
          meta: String(model.warnings.length),
          icon: 'alert',
        }),
      );
    }
  }

  /*
   * The workflow stage is the only thing here that depends on rig/mapping progress, and none of
   * those slices can change while MODEL is on screen — but they decide which step is lit the next
   * time this panel is drawn, so they are subscribed rather than read once.
   */
  return createPanel(container, ctx, render, {
    className: 'model',
    slices: [
      (s) => s.project,
      (s) => s.mapping.validation,
      (s) => s.rig.placement,
      (s) => s.rig.skinGenerated,
    ],
    events: ['model-changed'],
  });
}

export function mountProjectListPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  let projects: ProjectRecord[] = [];
  let sizes: Record<string, number> = {};
  let loading = true;

  function askDelete(project: ProjectRecord): void {
    runAsync(
      dialogs.confirm({
        title: 'Delete project',
        message: `Delete project "${project.name}"? This cannot be undone.`,
        confirmLabel: 'Delete',
        danger: true,
      }),
      (confirmed) => {
        if (confirmed) runAsync(controllers.project.deleteProject(project.id), refresh);
      },
    );
  }

  function render(root: HTMLElement): void {
    const current = ctx.store.getState().project?.id ?? null;
    const rows: HTMLElement[] = [];

    if (loading) rows.push(note('Loading…'));
    else if (projects.length === 0) rows.push(note('No saved projects yet.'));

    for (const project of projects) {
      rows.push(
        el(
          'div',
          {
            class: project.id === current ? 'row two-line sel' : 'row two-line',
            'data-project': project.id,
          },
          [
            icon('folder', 12),
            // Name + kind on line one, the metadata on its own line: a single project clipped its
            // own name at 320px when all three shared a line (audit B).
            el('span', { class: 'grow' }, [
              el('span', { text: project.name }),
              ' ',
              el('span', { class: 'dim', text: project.kind }),
              el('span', { class: 'sub', text: projectMeta(project, sizes[project.id]) }),
            ]),
            iconButton(
              'open',
              'Open',
              () => runAsync(controllers.project.openProject(project.id), refresh),
              { 'data-action': 'open-project', disabled: project.id === current },
            ),
            iconButton('trash', `Delete "${project.name}"`, () => askDelete(project), {
              'data-action': 'delete-project',
            }),
          ],
        ),
      );
    }

    rows.push(button([icon('refresh'), 'Refresh'], refresh));
    root.appendChild(section('Projects', rows, { icon: 'folder' }));
  }

  const instance = createPanel(container, ctx, render, {
    className: 'projects',
    slices: [(s) => s.project],
    events: ['model-changed'],
  });

  /**
   * Sizes are cosmetic: a controller that cannot produce them (or does not have the method at
   * all, as in a stubbed fixture) must cost the list nothing but the size column.
   */
  async function readSizes(list: readonly ProjectRecord[]): Promise<Record<string, number>> {
    try {
      return await controllers.project.glbSizes(list);
    } catch {
      return {};
    }
  }

  function refresh(): void {
    // Both outcomes must clear "Loading…" — a failed list still has to leave the panel usable.
    controllers.project.listProjects().then(
      async (list) => {
        // Most recently opened first, falling back to "last changed" for a project that predates
        // the field. Ties keep a stable order so the list does not shuffle between refreshes.
        projects = [...list].sort(
          (a, b) => recency(b) - recency(a) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
        );
        loading = false;
        instance.schedule();
        sizes = await readSizes(projects);
        instance.schedule();
      },
      () => {
        loading = false;
        instance.schedule();
      },
    );
  }

  refresh();
  return instance;
}
